import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Button, message, Card, Empty, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getUsers, updateUserInfo } from '../api'; 

const { Option } = Select;

const UserRole = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // 加载目标用户信息
  const loadUserInfo = async () => {
    try {
      setLoading(true);
      console.log(`开始加载用户 ID: ${id} 的信息`);
      // 适配后端返回格式：优先取 response.data（和 api.js 拦截器一致）
      const response = await getUsers();
      const userList = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
      const targetUser = userList.find(u => u.id == id); 

      if (!targetUser) {
        console.error(`未找到 ID 为 ${id} 的用户`);
        message.error('用户不存在');
        navigate('/user/list');
        return;
      }

      setUser(targetUser);
      // 回显数据：确保字段名和后端一致（user_type 是数字）
      form.setFieldsValue({
        id: targetUser.id,
        nickname: targetUser.nickname || '',
        phone: targetUser.phone || '',
        openid: targetUser.openid || '未绑定',
        mp_openid: targetUser.mp_openid || '未绑定',
        unionid: targetUser.unionid || '未绑定',
        user_type: targetUser.user_type || 0,
      });
      console.log(`加载用户信息成功：`, targetUser);
    } catch (error) {
      console.error('加载用户信息失败：', error);
      message.error('加载用户信息失败，请检查接口');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    if (id) {
      loadUserInfo();
    } else {
      message.error('缺少用户 ID');
      navigate('/user/list');
    }
  }, [id, form, navigate]);

  // 提交修改：确保参数格式和后端完全匹配
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log(`提交修改：用户 ID ${id} → `, values);
      setLoading(true);

      // 构造和后端匹配的参数（字段名完全一致）
      const submitData = {
        new_id: values.id ? Number(values.id) : undefined, // 新ID（仅测试环境使用）
        nickname: values.nickname || '', // 昵称非必填，空字符串兜底
        phone: values.phone ? values.phone.trim() : '',      // 手机号去空格，避免首尾空格导致校验失败
        user_type: Number(values.user_type) // 确保是数字类型，匹配后端 int8
      };

      // 调用更新接口
      const res = await updateUserInfo(id, submitData);
      // 校验后端返回结果（适配 success/msg 格式）
      if (res?.success) {
        message.success('用户信息修改成功');
        navigate('/user/list');
      } else {
        message.error(res?.msg || '修改用户信息失败');
      }
    } catch (error) {
      console.error('修改用户信息失败：', error);
      // 区分前端校验错误和接口错误
      if (error?.errorFields) {
        message.error('请检查表单填写是否正确');
      } else {
        message.error('修改用户信息失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载中状态
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  // 用户不存在状态
  if (!user) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Empty description="用户不存在" /></div>;
  }

  return (
    <Card 
      title={
        <Space>
          修改用户信息
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/user/list')}
            style={{ fontSize: '14px', color: '#1890ff' }}
          >
            返回列表
          </Button>
        </Space>
      } 
      style={{ maxWidth: 500, margin: '20px auto', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)' }}
    >
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSubmit} 
        initialValues={{ user_type: user.user_type }}
        // 禁用表单自动提交，避免回车误触
        autoComplete="off"
      >
        {/* 用户ID：测试用，可编辑，传给后端作为 new_id */}
        <Form.Item
          name="id"
          label="用户ID（测试用，可修改）"
          rules={[
            { required: true, message: '用户ID不能为空' },
          ]}
        >
          <Input placeholder="请输入新的用户ID（仅测试环境使用）" style={{ borderRadius: 4 }} />
        </Form.Item>

        {/* 昵称：非必填，保留原有输入 */}
        <Form.Item
          name="nickname"
          label="用户昵称"
          rules={[
            { max: 20, message: '昵称长度不能超过20个字' } // 匹配后端 min=2,max=20 规则
          ]}
        >
          <Input placeholder="请输入用户昵称（选填）" style={{ borderRadius: 4 }} />
        </Form.Item>

        {/* 手机号：必填 + 严格格式校验（匹配后端 regexp=^1[3-9]\\d{9}$） */}
        <Form.Item
          name="phone"
          label="手机号"
          rules={[
            { required: true, message: '手机号不能为空' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的11位手机号（如：13800138000）' },
            { max: 11, message: '手机号长度不能超过11位' }
          ]}
          // 回显时自动去空格，避免格式问题
          getValueFromEvent={(e) => e.target.value.trim()}
        >
          <Input 
            placeholder="请输入手机号（必填）" 
            style={{ borderRadius: 4 }} 
            maxLength={11} // 限制输入长度
          />
        </Form.Item>

        {/* OpenID：只读，禁用编辑 */}
        <Form.Item
          name="openid"
          label="OpenID"
          // 标记为无需校验，避免表单提交时校验
          rules={[{ required: false }]}
        >
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        {/* 公众号OpenID：只读 */}
        <Form.Item
          name="mp_openid"
          label="公众号OpenID"
          rules={[{ required: false }]}
        >
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        {/* UnionID：只读 */}
        <Form.Item
          name="unionid"
          label="UnionID"
          rules={[{ required: false }]}
        >
          <Input disabled placeholder="未绑定" style={{ borderRadius: 4, backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        {/* 用户角色：必填，确保是数字类型 */}
        <Form.Item
          name="user_type"
          label="用户角色"
          rules={[
            { required: true, message: '请选择用户角色' },
            { type: 'number', message: '角色类型必须是数字' }
          ]}
        >
          <Select 
            placeholder="请选择角色" 
            style={{ borderRadius: 4 }}
            // 确保返回数字类型
            onChange={(value) => form.setFieldsValue({ user_type: Number(value) })}
          >
            <Option value={1}>房东</Option>
            <Option value={2}>租客</Option>
            <Option value={3}>管理员</Option>
          </Select>
        </Form.Item>

        {/* 提交按钮 */}
        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading} 
            block
            style={{ borderRadius: 4, height: 40, fontSize: 16 }}
          >
            提交修改
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default UserRole;